import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users, Search } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';

const SendmireinSubscribers = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [filter, setFilter] = useState('all'); // all, subscribed, unsubscribed
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (token) loadSubscribers();
  }, [filter, token]);

  const loadSubscribers = async () => {
    try {
      setLoading(true);
      const filterParam = filter === 'all' ? undefined : (filter === 'subscribed' ? 'true' : 'false');
      const response = await api.newslettersSubscribersList(token, filterParam);
      setSubscribers(response);
    } catch (error) {
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.subscribers.loadError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const maskEmail = (email) => {
    if (!email) return '-';
    const [local, domain] = email.split('@');
    if (!domain) return '***';
    
    // Show first 2 and last 1 character of local part
    const maskedLocal = local.length > 3 
      ? local.substring(0, 2) + '***' + local.slice(-1)
      : '***';
    
    // Mask domain except TLD
    const [domainName, tld] = domain.split('.');
    const maskedDomain = domainName.length > 2
      ? domainName.substring(0, 1) + '***.' + tld
      : '***.' + tld;
    
    return maskedLocal + '@' + maskedDomain;
  };

  const maskName = (name) => {
    if (!name) return '-';
    if (name.length <= 2) return '***';
    return name.substring(0, 2) + '***';
  };

  const filteredSubscribers = subscribers.filter(sub => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      sub.endUser.email.toLowerCase().includes(term) ||
      sub.endUser.name.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-muted p-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Users className="w-8 h-8" />
            {t('sendmirein.subscribers.title')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('sendmirein.subscribers.subtitle')}</p>
        </div>
        <Card className="p-6">
          {/* Controls */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('sendmirein.subscribers.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-border rounded-md"
              >
                <option value="all">{t('sendmirein.subscribers.filter.all')}</option>
                <option value="subscribed">{t('sendmirein.subscribers.filter.subscribed')}</option>
                <option value="unsubscribed">{t('sendmirein.subscribers.filter.unsubscribed')}</option>
              </select>

              <Button
                variant="outline"
                onClick={() => navigate('/sendmirein')}
              >
                {t('sendmirein.subscribers.back')}
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-muted-foreground">{t('sendmirein.subscribers.stats.total')}</div>
              <div className="text-2xl font-bold">{subscribers.length}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm text-muted-foreground">{t('sendmirein.subscribers.stats.subscribed')}</div>
              <div className="text-2xl font-bold">
                {subscribers.filter(s => s.subscribed).length}
              </div>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <div className="text-sm text-muted-foreground">{t('sendmirein.subscribers.stats.unsubscribed')}</div>
              <div className="text-2xl font-bold">
                {subscribers.filter(s => !s.subscribed).length}
              </div>
            </div>
          </div>

          {/* Table */}
          {filteredSubscribers.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {t('sendmirein.subscribers.none')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">{t('sendmirein.subscribers.table.email')}</th>
                    <th className="text-left py-3 px-4">{t('sendmirein.subscribers.table.name')}</th>
                    <th className="text-left py-3 px-4">{t('sendmirein.subscribers.table.status')}</th>
                    <th className="text-left py-3 px-4">{t('sendmirein.subscribers.table.subscribedAt')}</th>
                    <th className="text-left py-3 px-4">{t('sendmirein.subscribers.table.unsubscribedAt')}</th>
                    <th className="text-left py-3 px-4">{t('sendmirein.subscribers.table.source')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="border-b hover:bg-muted">
                      <td className="py-3 px-4 font-medium font-mono text-sm">
                        {maskEmail(subscriber.endUser.email)}
                      </td>
                      <td className="py-3 px-4">
                        {maskName(subscriber.endUser.name)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          subscriber.subscribed 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-muted text-foreground'
                        }`}>
                          {subscriber.subscribed ? t('sendmirein.subscribers.badge.subscribed') : t('sendmirein.subscribers.badge.unsubscribed')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatDate(subscriber.subscribedAt)}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatDate(subscriber.unsubscribedAt)}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {subscriber.source || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SendmireinSubscribers;
